import { almostZero, Phone } from "@xola/ui-kit";
import { ValueFormatterParams } from "ag-grid-community";
import { DateFormat, formatDate } from "common";
import { startCase, sumBy } from "lodash";
import React from "react";
import {
    AbandonedPurchaseItem,
    AbandonItemStatus,
    ExperiencePurchaseItem,
    LineItem,
    LineItemType,
    Report,
} from "types";
import { Currency } from "../../../Common/Currency";
import { ProductRenderer } from "../ColumnTypes";
import { CustomProductFilter } from "../CustomFilters/CustomProductFilter";
import { CustomReport } from "../CustomReport";
import { TextFilterParams } from "../ReportFilterState";
import { ReportColumnDef, ReportColumnFilterValueType } from "../SuggestedReports";

export const AbandonedReport = ({ report }: { report: Report }) => {
    const columns = React.useMemo(
        () =>
            getColumnDefs({
                report,
            }),
        [report],
    );

    return <CustomReport dateFields={["createdAt"]} report={report} columns={columns} />;
};

interface LineItemTotalRendererProps {
    data: ExperiencePurchaseItem;
    lineItemType: LineItemType[];
    isCurrency?: boolean;
    colDef: ReportColumnDef;
}

const LineItemTotalRenderer = ({ data, lineItemType, colDef, isCurrency }: LineItemTotalRendererProps) => {
    const total = sumBy(data.lineItems, (lineItem) => {
        const isCorrectItem = lineItemType.includes(lineItem.type);
        const value = lineItem.quantity ?? lineItem.amount;
        return isCorrectItem && lineItem.name === colDef?.headerName ? value : 0;
    });

    if (almostZero(total)) {
        return null;
    }

    return isCurrency ? <Currency shouldRemoveTrailingZeroes={false}>{total}</Currency> : total;
};

type GetColumnDefsParams = {
    report: Report;
};
const getColumnDefs = ({ report }: GetColumnDefsParams): ReportColumnDef[] => [
    {
        field: "id",
        headerName: "Reservation ID",
    },
    {
        field: "purchase.id",
        headerName: "Purchase ID",
    },
    {
        field: "product",
        headerName: "Product",
        type: "product",
        filter: CustomProductFilter,
        filterParams: { report },
        cellRenderer: ProductRenderer,
    },
    {
        field: "draftCreatedAt",
        headerName: "Booking Date",
        type: "datetime",
        filterValueType: ReportColumnFilterValueType.DATE,
        filterParams: {
            report,
        },
    },
    {
        field: "createdAt",
        headerName: "Abandoned Date",
        type: "datetime",
        filterValueType: ReportColumnFilterValueType.DATE,
        filterParams: {
            report,
        },
    },
    {
        field: "emailedAt",
        headerName: "Emailed Date",
        type: "datetime",
        filterValueType: ReportColumnFilterValueType.DATE,
        filterParams: {
            report,
        },
        valueGetter: ({ data }: { data: AbandonedPurchaseItem }) => {
            const audits = data.audits ?? [];
            const emailedAudit = audits.find((audit) => {
                const status = audit.data?.status?.new;
                return [AbandonItemStatus.AUTO_EMAILED, AbandonItemStatus.EMAILED].includes(status);
            });

            return emailedAudit?.createdAt ? formatDate(emailedAudit.createdAt, DateFormat.DATE_TIME_SHORT) : "";
        },
    },
    {
        field: "purchase.convertedAt",
        headerName: "Conversion Date",
        type: "datetime",
        filterValueType: ReportColumnFilterValueType.DATE,
        filterParams: {
            report,
        },
        // cellRenderer: () => "// TODO",
    },
    {
        field: "arrivalDate",
        headerName: "Arrival Date",
        type: "date",
        filterValueType: ReportColumnFilterValueType.DATE,
        filterParams: {
            report,
        },
    },
    {
        field: "arrivalTime",
        headerName: "Arrival Time",
        filter: "agNumberColumnFilter",
        type: "arrivalTime",
        filterParams: {
            maxNumConditions: 1,
        },
    },
    {
        field: "organizer.name",
        headerName: "Customer Name",
        minWidth: 200,
        filterValueType: ReportColumnFilterValueType.STRING,
        filter: "agTextColumnFilter",
        filterParams: TextFilterParams,
    },
    {
        field: "organizer.email",
        headerName: "Customer Email",
        minWidth: 200,
        filterValueType: ReportColumnFilterValueType.STRING,
        filter: "agTextColumnFilter",
        filterParams: TextFilterParams,
    },
    {
        field: "purchase.phone",
        headerName: "Phone",
        minWidth: 200,
        cellRenderer: ({ value }: { value: string }) => (value ? <Phone>{value}</Phone> : ""),
    },
    {
        field: "status",
        headerName: "Status",
        minWidth: 200,
        valueFormatter: (params: ValueFormatterParams) => startCase(params.value),
    },
    {
        field: "quantity",
        headerName: "Total Booked",
        type: "number",
        minWidth: 120,
        filterParams: {
            maxNumConditions: 1,
        },
        expand: {
            key: "lineItems.*",
            type: "number",
            minWidth: 130,
            headerGetter: (lineItems: LineItem[]) =>
                getMatchingHeaders(lineItems, [LineItemType.DEMOGRAPHIC, LineItemType.DEMOGRAPHIC_SECONDARY]),
            cellRenderer: LineItemTotalRenderer,
            cellRendererParams: { lineItemType: [LineItemType.DEMOGRAPHIC, LineItemType.DEMOGRAPHIC_SECONDARY] },
        },
    },
    {
        field: "equipment",
        headerName: "Equipment Booked",
        type: "number",
        minWidth: 120,
        filter: false,
        expand: {
            key: "lineItems.*",
            type: "number",
            minWidth: 130,
            headerGetter: (lineItems: LineItem[]) => getMatchingHeaders(lineItems, [LineItemType.EQUIPMENT]),
            cellRenderer: LineItemTotalRenderer,
            cellRendererParams: { lineItemType: [LineItemType.EQUIPMENT] },
        },
    },
    {
        field: "amount",
        headerName: "Total Value",
        type: "currency",
        filterParams: {
            maxNumConditions: 1,
        },
    },
];

const getMatchingHeaders = (lineItems: LineItem[], types: LineItemType[]): string[] => {
    return lineItems.filter((lineItem) => types.includes(lineItem.type)).map((lineItem) => lineItem.name);
};
